import { useEffect, useState } from 'react';
import moment from 'moment';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { useAppContext } from '../../../contexts/AppContext';
import { useParamsContext } from '../../../contexts/ParamsContext';
import { formatBRL } from '../../agenda/cliente/List/helpers';
import { useEstoqueContext } from '../Context';
import { EstoqueService } from '../Service';
import { ITEM_TYPE_LABELS, MOVEMENT_TYPE_LABELS, formatQuantity, isLowStock } from '../units';
import ItemForm from './ItemForm';
import { PackageOpen, TriangleAlert } from 'lucide-react';

const service = new EstoqueService();

const getMovementSign = (movement: any) => {
    if (movement?.type === 'ENTRADA') return '+';
    if (movement?.type === 'AJUSTE' && movement?.adjustmentDirection === 'IN') return '+';
    return '−';
};

const formatPrice = (value: unknown) => (value === undefined || value === null ? '—' : formatBRL(Number(value) || 0));

export default function EstoqueItemDetail() {
    const { id, setParams } = useParamsContext();
    const { successSnackbar, errorSnackbar } = useAppContext();
    const { reloadSummary, reloadRecommendations } = useEstoqueContext();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [item, setItem] = useState<any>(null);
    const [editOpen, setEditOpen] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const load = async () => {
        try {
            setError(false);
            const result = await service.getItem(id).then((r) => r?.data || null);
            setItem(result);
        } catch (e) {
            console.error(e);
            setError(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setLoading(true);
        void load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleDelete = async () => {
        try {
            setDeleting(true);
            await service.deleteItem(item._id);
            successSnackbar('Item removido.');
            void reloadSummary();
            void reloadRecommendations();
            setParams({ action: 'items', id: '' });
        } catch (e: any) {
            console.error(e);
            errorSnackbar(e?.response?.data?.message || 'Não foi possível remover o item.');
            setDeleting(false);
            setOpenDelete(false);
        }
    };

    if (loading) {
        return (
            <div className="space-y-3" aria-busy="true">
                <div className="sf-skeleton h-24" />
                <div className="sf-skeleton h-40" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="sf-state">
                <TriangleAlert aria-hidden />
                <h2>Não foi possível carregar o item</h2>
                <button type="button" className="btn btn-primary btn-sm" onClick={load}>
                    Tentar novamente
                </button>
            </div>
        );
    }

    if (!item) {
        return (
            <div className="sf-state">
                <PackageOpen aria-hidden />
                <h2>Item não encontrado</h2>
                <button type="button" className="sf-link" onClick={() => setParams({ action: 'items', id: '' })}>
                    Voltar para itens
                </button>
            </div>
        );
    }

    const movements: any[] = item.movements || [];
    const low = item.isActive !== false && isLowStock(item);

    return (
        <div className="grid gap-6 xl:grid-cols-2 xl:items-start">
            <div className="space-y-6">
                <header>
                    <h2 className="text-xl font-semibold text-base-content">{item.name}</h2>
                    <div className="sf-tags">
                        <span className="sf-tag" data-tone="gray">
                            {ITEM_TYPE_LABELS[item.type] || item.type}
                        </span>
                        {low && (
                            <span className="sf-tag" data-tone="red">
                                Baixo estoque
                            </span>
                        )}
                        {item.isActive === false && (
                            <span className="sf-tag" data-tone="gray">
                                Inativo
                            </span>
                        )}
                    </div>
                </header>

                <section>
                    <h3 className="sf-group-title">Saldo</h3>
                    <div className="sf-group">
                        <div className="sf-info-row">
                            <span>Em estoque</span>
                            <span>
                                {formatQuantity(item.balance?.onHand)} {item.unit}
                            </span>
                        </div>
                        <div className="sf-info-row">
                            <span>Reservado</span>
                            <span>{formatQuantity(item.balance?.reserved)}</span>
                        </div>
                        <div className="sf-info-row">
                            <span>Disponível</span>
                            <span>{formatQuantity(item.balance?.available)}</span>
                        </div>
                    </div>
                </section>

                <div className="flex flex-wrap gap-2">
                    {item.isActive !== false && (
                        <button
                            type="button"
                            className="btn btn-primary btn-sm flex-1 sm:flex-none"
                            onClick={() => setParams({ action: 'movement', id: item._id })}
                        >
                            Registrar movimentação
                        </button>
                    )}
                    <button type="button" className="btn btn-ghost btn-sm flex-1 sm:flex-none" onClick={() => setEditOpen(true)}>
                        Editar item
                    </button>
                </div>

                <section>
                    <h3 className="sf-group-title">Informações</h3>
                    <div className="sf-group">
                        <div className="sf-info-row">
                            <span>Marca</span>
                            <span>{item.brand || '—'}</span>
                        </div>
                        <div className="sf-info-row">
                            <span>SKU</span>
                            <span>{item.sku || '—'}</span>
                        </div>
                        <div className="sf-info-row">
                            <span>Estoque mínimo</span>
                            <span>{item.minStock ?? '—'}</span>
                        </div>
                        <div className="sf-info-row">
                            <span>Ponto de reposição</span>
                            <span>{item.reorderPointManual ?? '—'}</span>
                        </div>
                        <div className="sf-info-row">
                            <span>Prazo de entrega</span>
                            <span>{item.leadTimeDays !== undefined && item.leadTimeDays !== null ? `${item.leadTimeDays} dias` : '—'}</span>
                        </div>
                        <div className="sf-info-row">
                            <span>Preço de custo</span>
                            <span>{formatPrice(item.costPrice)}</span>
                        </div>
                        {item.type === 'REVENDA' && (
                            <div className="sf-info-row">
                                <span>Preço de venda</span>
                                <span>{formatPrice(item.salePrice)}</span>
                            </div>
                        )}
                    </div>
                </section>

                <div className="sf-group">
                    <button type="button" className="sf-destructive-row" onClick={() => setOpenDelete(true)}>
                        Remover item
                    </button>
                </div>
            </div>

            <section>
                <h3 className="sf-group-title">Movimentos recentes</h3>
                {movements.length === 0 ? (
                    <p className="px-1 text-sm text-base-content/55">Nenhuma movimentação registrada.</p>
                ) : (
                    <ul className="sf-group">
                        {movements.map((movement) => (
                            <li key={movement._id}>
                                <div className="sf-row">
                                    <span className="sf-row-body">
                                        <span className="sf-row-title">
                                            {MOVEMENT_TYPE_LABELS[movement.type] || movement.type}
                                        </span>
                                        <span className="sf-row-sub">
                                            {[moment(movement.occurredAt).format('DD/MM/YYYY [às] HH:mm'), movement.reasonCode]
                                                .filter(Boolean)
                                                .join(' · ')}
                                        </span>
                                        {!!movement.notes && <span className="sf-row-sub">{movement.notes}</span>}
                                    </span>
                                    <span className="sf-row-end">
                                        {getMovementSign(movement)}
                                        {formatQuantity(movement.quantity)}
                                    </span>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            <ItemForm open={editOpen} initialData={item} onClose={() => setEditOpen(false)} onSaved={load} />

            {openDelete && (
                <ConfirmDialog
                    title="Remover item?"
                    confirmLabel="Remover"
                    loading={deleting}
                    onConfirm={handleDelete}
                    onClose={() => setOpenDelete(false)}
                >
                    {item.name} sai das listas do estoque. Movimentos já registrados são mantidos. Para apenas pausar o uso,
                    desative o item na edição.
                </ConfirmDialog>
            )}
        </div>
    );
}
